import { mdiChevronLeft } from '@mdi/js';
import Icon from '@mdi/react';
import React, { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  TICKET_STATUS_BADGE_STYLES,
  TICKET_STATUS_TYPES,
  CLOSED_TICKET_STATUS,
} from './constants';
import { CancelTicket, ReassignTicket } from '../../../components/Ticket';
import {
  DisplayModal,
  ModalMultiWrapper,
  Badge,
  SpinnerLoading,
} from '../../../components/Common';
import { useTicketDetails } from '../context/TicketDetailsProvider';
import { getAuthUser } from '../../../utils/auth.util';
import TicketDetailView from '../../../components/Ticket/TicketDetailView/TicketDetailView';
import CommentSection from '../../../components/Ticket/CommentSection/CommentSection';
import { ROLE_NAMES } from '../../../common/constant';
import { DateUtil } from '../../../utils';

import './TicketDetails.scss';

function TicketDetails() {
  document.title = 'Ticket Details | Envision Daikin';
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const fr = queryParams.get('fr');
  const userEmployeeId = getAuthUser()?.employeeId;
  const {
    ticketDetails,
    roleType,
    displayModalOpen,
    displayModalFile,
    setDisplayModalOpen,
    TICKET_ROLE_TYPES,
    updateTicketMutation,
    error: ticketDetailsError,
    isLoading: isLoadingData,
    ticketResponseListingLoading,
  } = useTicketDetails();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { id: ticketId } = useParams();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modelName, setModelName] = useState('');

  const roleName = getAuthUser()?.role?.Name;

  if (ticketDetailsError) {
    toast.error('Failed to fetch ticket details. Please try again.', {
      className: 'toast-error',
    });
    navigate(fr === 'HR' ? '/hr/tickets' : '/tickets');
  }

  const navigateBack = fromTab => {
    if (!fromTab) {
      navigate('/tickets');
      return;
    }
    if (fromTab === '3') {
      navigate(`/hr/tickets?t=${fromTab}`);
      return;
    }
    navigate(`/tickets?t=${fromTab}`);
  };

  useEffect(() => {
    if (!ticketDetails) {
      return;
    }
    if (roleName === ROLE_NAMES.ADMIN) {
      return;
    }
    if (roleName === ROLE_NAMES.SELF_SERVICE) {
      if (
        ticketDetails?.RequestorCd.toLowerCase() !==
        userEmployeeId?.toLowerCase()
      ) {
        navigate('/tickets');
      }
    }
  }, [ticketDetails]);

  // Loading state
  if (isLoadingData || ticketResponseListingLoading) {
    return <SpinnerLoading spinnerSize="2rem" height="100vh" />;
  }

  return (
    <div className="page-content ticket-details px-0">
      <div className="ticket-details-header d-flex flex-row align-items-center justify-content-between pt-2">
        <div className="d-flex flex-row align-items-center gap-2 ps-3">
          <button
            onClick={() => {
              navigateBack(fr);
            }}
            type="button"
            className="btn btn-link p-0 d-flex flex-row align-items-center gap-1 back-btn"
          >
            <Icon path={mdiChevronLeft} size={1.0} />
            <span className="back fs-6">Back</span>
          </button>
          <div className="ticket-details-separator" />
          <div className="d-flex flex-row align-items-center gap-2">
            <span className="fs-6">Ticket ID</span>
            <span className="fw-semibold text-dark fs-6">{ticketId}</span>
          </div>
          <div className="ticket-details-separator" />
          <div className="d-flex flex-row align-items-center gap-2">
            <span className="fs-6">Status</span>
            <Badge
              config={TICKET_STATUS_BADGE_STYLES[ticketDetails?.Status]}
              labelType="TitleCase"
              badgeType="pill"
              className="fs-6"
            >
              {ticketDetails?.Status}
            </Badge>
          </div>
          <div className="ticket-details-separator" />
          <div className="d-flex flex-row align-items-center gap-2">
            <span className="fs-6">Created On</span>
            <div className="ticket-created-badge">
              {DateUtil.isoToDdMmYyyySlice(ticketDetails?.CreatedOn)}
            </div>
            {CLOSED_TICKET_STATUS.includes(ticketDetails?.Status) && (
              <>
                <div className="ticket-details-separator" />
                <span className="fs-6">Closed On</span>
                <div className="ticket-created-badge">
                  {DateUtil.isoToDdMmYyyySlice(ticketDetails?.StatusLastDate)}
                </div>
              </>
            )}
          </div>
        </div>
        <div>
          <div className="d-flex flex-row align-items-center gap-2 px-3">
            {roleType === TICKET_ROLE_TYPES.CREATED_BY && (
              <div className="d-flex flex-row align-items-center gap-2">
                <button
                  onClick={() => {
                    setModelName('cancelTicketModal');
                    setIsModalOpen(true);
                  }}
                  type="button"
                  className="ticket-details-cancel custom-btn"
                  disabled={
                    ticketDetails?.Status === TICKET_STATUS_TYPES.CANCELLED ||
                    ticketDetails?.Status === TICKET_STATUS_TYPES.COMPLETED
                  }
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="d-flex flex-row align-items-start">
        <div className="w-50">
          <TicketDetailView />
        </div>
        <div className="w-50">
          <CommentSection />
        </div>
      </div>
      <DisplayModal
        isModalOpen={displayModalOpen}
        toggle={() => setDisplayModalOpen(false)}
        title="View Attachments"
        file={displayModalFile}
      />
      <ModalMultiWrapper
        modals={{
          reassignTicketModal: {
            component: ReassignTicket,
            props: {
              handleOnSubmit: async (payload, reset) => {
                try {
                  await updateTicketMutation.mutateAsync({
                    ticketNo: ticketId,
                    ...payload,
                  });
                  reset();
                  toast.success(
                    `Ticket #${ticketId} reassigned successfully.`,
                    {
                      className: 'toast-success',
                    },
                  );
                  queryClient.invalidateQueries(['ticketDetails', ticketId]);
                  setIsModalOpen(false);
                } catch (error) {
                  if (error?.error) {
                    toast.error(error?.error, {
                      className: 'toast-error',
                    });
                  } else {
                    toast.error(
                      'Failed to reassign ticket. Please try again.',
                      {
                        className: 'toast-error',
                      },
                    );
                  }
                }
              },
            },
          },
          cancelTicketModal: {
            component: CancelTicket,
            props: {
              handleOnSubmit: async payload => {
                if (roleType !== TICKET_ROLE_TYPES.CREATED_BY) {
                  toast.error('Only the creator can cancel the ticket.', {
                    className: 'toast-error',
                  });
                  return;
                }
                try {
                  await updateTicketMutation.mutateAsync({
                    ticketNo: ticketId,
                    ticketStatus: TICKET_STATUS_TYPES.CANCELLED,
                    ...payload,
                  });
                  toast.success(`Ticket #${ticketId} cancelled successfully.`, {
                    className: 'toast-success',
                  });
                  navigateBack(fr);
                } catch (error) {
                  if (error?.error) {
                    toast.error(error?.error, {
                      className: 'toast-error',
                    });
                  } else {
                    toast.error('Failed to cancel ticket. Please try again.', {
                      className: 'toast-error',
                    });
                  }
                }
              },
            },
          },
        }}
        isOpen={isModalOpen}
        toggle={() => setIsModalOpen(false)}
        selectedModal={modelName}
      />
    </div>
  );
}

export default TicketDetails;
